import {
	getFormProps,
	getInputProps,
	getSelectProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { captureException } from '@sentry/react-router'
import { eq } from 'drizzle-orm'
import { useState } from 'react'
import {
	Form,
	data,
	useActionData,
	useLoaderData,
	useNavigate,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	type MetaFunction,
} from 'react-router'

import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { redirectBack } from 'remix-utils/redirect-back'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { users } from '#app/db/schema.ts'
import { createContact } from '#app/services/resend-contacts.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { cn } from '#app/utils/cn.ts'
import { db } from '#app/utils/db.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { useIsPending } from '#app/utils/use-pending.tsx'

const INTENT_OPTIONS = [
	'Just me / side project',
	'Freelance / client work',
	'Small organization',
	'Large organization / Enterprise',
	'Not sure yet',
] as const

const USAGE_OPTIONS = [
	'100 – 500',
	'500 – 1,000',
	'1,000 – 5,000',
	'5,000+',
	'Not sure yet',
] as const

const PRICE_EXPECTATIONS_OPTIONS = [
	'$10 – 15',
	'$15 – 25',
	'$25 – 40',
	'$40+',
	'Not sure yet',
] as const

const PricingFormSchema = z.object({
	email: z
		.string({ required_error: 'Email is required' })
		.email('Please enter a valid email'),
	intent: z.enum(INTENT_OPTIONS).optional(),
	usage: z.enum(USAGE_OPTIONS).optional(),
	priceExpectations: z.enum(PRICE_EXPECTATIONS_OPTIONS).optional(),
	feedback: z.string().optional(),
	source: z.string().default('pricing-page'),
})

export const meta: MetaFunction = () => [
	{ title: 'Waitlist | xcelera' },
	{
		name: 'description',
		content:
			'Join the waitlist for xcelera paid plans with usage-based pricing and higher limits.',
	},
]

export async function loader({ request }: LoaderFunctionArgs) {
	if (!process.env.RESEND_SEGMENT_ID_PAID_PLANS) {
		throw new Error('No Resend segment ID configured for paid plans waitlist.')
	}

	const userId = await getUserId(request)
	const user = userId
		? await db
				.select({ email: users.email })
				.from(users)
				.where(eq(users.id, userId))
				.get()
		: null

	return { defaultEmail: user?.email }
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: PricingFormSchema,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { email, intent, usage, priceExpectations, feedback, source } =
		submission.value

	const properties: Record<string, string> = { source }
	if (intent) properties.intent = intent
	if (usage) properties.usage = usage
	if (priceExpectations) properties.priceExpectations = priceExpectations
	if (feedback) properties.feedback = feedback

	const segmentId = process.env.RESEND_SEGMENT_ID_PAID_PLANS
	if (!segmentId) {
		throw new Error('No Resend segment ID configured for paid plans waitlist.')
	}

	const result = await createContact({
		email,
		properties,
		segmentId,
	})

	if (result.status === 'error') {
		captureException(
			new Error(`Resend contact creation failed: ${result.error.message}`, {
				cause: result.error,
			}),
		)
		return data(
			{
				result: submission.reply({
					formErrors: [
						'Something went wrong joining the waitlist. Please try again.',
					],
				}),
			},
			{ status: 500 },
		)
	}

	const backResponse = redirectBack(request, { fallback: '/pricing' })
	const location = new Headers(backResponse.init?.headers).get('Location')
	const redirectTo = safeRedirect(location, '/pricing')

	return redirectWithToast(redirectTo, {
		type: 'success',
		title: "You're on the list!",
		description: 'Thanks — we will email you when paid plans are available.',
	})
}

export default function WaitlistRoute() {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const loaderData = useLoaderData<typeof loader>()
	const navigate = useNavigate()

	const [form, fields] = useForm({
		id: 'waitlist-form',
		constraint: getZodConstraint(PricingFormSchema),
		lastResult: actionData?.result,
		defaultValue: {
			email: loaderData.defaultEmail,
		},
		shouldRevalidate: 'onBlur',
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: PricingFormSchema })
		},
	})

	const { email, intent, usage, priceExpectations, feedback, source } = fields

	const [intentValue, setIntentValue] = useState(
		(intent.initialValue as string) ?? '',
	)
	const [usageValue, setUsageValue] = useState(
		(usage.initialValue as string) ?? '',
	)
	const [priceExpectationsValue, setPriceExpectationsValue] = useState(
		(priceExpectations.initialValue as string) ?? '',
	)

	const { defaultValue: _intentDefault, ...intentProps } =
		getSelectProps(intent)
	const { defaultValue: _usageDefault, ...usageProps } = getSelectProps(usage)
	const { defaultValue: _priceExpectationsDefault, ...priceExpectationsProps } =
		getSelectProps(priceExpectations)

	const inputStyles =
		'focus-visible:ring-primary mt-1 bg-background dark:bg-input border-border font-mono tracking-tight transition-colors duration-100 focus-visible:ring-1 focus-visible:ring-offset-0'

	return (
		<main className="container mx-auto max-w-2xl px-4 py-16">
			<div className="space-y-8">
				<div className="text-center">
					<h1 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
						Join the waitlist for paid plans
					</h1>
					<p className="text-muted-foreground mx-auto mt-4 max-w-xl text-lg leading-relaxed">
						We're building flexible usage-based plans for professionals and
						teams. Join the waitlist to unlock higher limits and help us define
						fair pricing.
					</p>
					<p className="mt-2">No spam. Just an email when we launch.</p>
				</div>

				<div className="bg-muted/50 rounded-lg border p-8">
					<Form method="post" className="space-y-6" {...getFormProps(form)}>
						<HoneypotInputs />
						<input {...getInputProps(source, { type: 'hidden' })} />

						<div className="flex flex-col gap-1.5">
							<Label
								htmlFor={email.id}
								className="text-foreground text-sm font-medium tracking-wide"
							>
								Email
							</Label>
							<Input
								{...getInputProps(email, { type: 'email' })}
								placeholder="you@example.com"
								className={inputStyles}
								autoFocus
							/>
							{email.errors && (
								<div className="text-destructive text-sm">{email.errors}</div>
							)}
						</div>

						<div className="flex flex-col gap-1.5">
							<Label
								htmlFor={intent.id}
								className="text-foreground text-sm font-medium tracking-wide"
							>
								How do you plan to use xcelera?
							</Label>
							<Select
								value={intentValue}
								onValueChange={setIntentValue}
								{...intentProps}
							>
								<SelectTrigger
									id={intent.id}
									className={cn(inputStyles, 'w-72')}
								>
									<SelectValue placeholder="Select an option (optional)" />
								</SelectTrigger>
								<SelectContent className="border-border bg-popover">
									{INTENT_OPTIONS.map((option) => (
										<SelectItem
											key={option}
											value={option}
											className="text-popover-foreground"
										>
											{option}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{intent.errors && (
								<div className="text-destructive text-sm">{intent.errors}</div>
							)}
						</div>

						<div className="flex flex-col gap-1.5">
							<Label
								htmlFor={usage.id}
								className="text-foreground text-sm font-medium tracking-wide"
							>
								How many monthly audits do you need?
							</Label>
							<Select
								value={usageValue}
								onValueChange={setUsageValue}
								{...usageProps}
							>
								<SelectTrigger
									id={usage.id}
									className={cn(inputStyles, 'w-72')}
								>
									<SelectValue placeholder="Select usage (optional)" />
								</SelectTrigger>
								<SelectContent className="border-border bg-popover">
									{USAGE_OPTIONS.map((option) => (
										<SelectItem
											key={option}
											value={option}
											className="text-popover-foreground"
										>
											{option}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{usage.errors && (
								<div className="text-destructive text-sm">{usage.errors}</div>
							)}
						</div>

						<div className="flex flex-col gap-1.5">
							<Label
								htmlFor={priceExpectations.id}
								className="text-foreground text-sm font-medium tracking-wide"
							>
								What monthly price feels reasonable?
							</Label>
							<Select
								value={priceExpectationsValue}
								onValueChange={setPriceExpectationsValue}
								{...priceExpectationsProps}
							>
								<SelectTrigger
									id={priceExpectations.id}
									className={cn(inputStyles, 'w-72')}
								>
									<SelectValue placeholder="Select price range (optional)" />
								</SelectTrigger>
								<SelectContent className="border-border bg-popover">
									{PRICE_EXPECTATIONS_OPTIONS.map((option) => (
										<SelectItem
											key={option}
											value={option}
											className="text-popover-foreground"
										>
											{option}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{priceExpectations.errors && (
								<div className="text-destructive text-sm">
									{priceExpectations.errors}
								</div>
							)}
						</div>

						<div className="flex flex-col gap-1.5">
							<Label
								htmlFor={feedback.id}
								className="text-foreground text-sm font-medium tracking-wide"
							>
								Any other feedback or requests?
							</Label>
							<Textarea
								{...getInputProps(feedback, {
									type: 'text',
								})}
								placeholder="Optional feedback or feature requests..."
								className={inputStyles}
								rows={4}
							/>
							{feedback.errors && (
								<div className="text-destructive text-sm">
									{feedback.errors}
								</div>
							)}
						</div>

						<div className="pt-4">
							<div className="flex items-center gap-4">
								<button
									type="button"
									className="text-muted-foreground hover:text-foreground text-sm font-semibold transition-colors"
									onClick={() =>
										window.history.length > 1 ? navigate(-1) : navigate('/')
									}
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={isPending}
									className={cn(
										'bg-foreground text-background hover:bg-foreground/90 focus-visible:ring-foreground rounded-md px-4 py-2 text-sm font-semibold shadow-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
										isPending && 'cursor-wait',
									)}
								>
									{isPending ? 'Submitting...' : 'Notify me'}
								</button>
							</div>
						</div>

						{form.errors && (
							<div className="bg-destructive/10 text-destructive border-destructive/20 rounded-md border px-4 py-3 text-center text-sm font-medium">
								{form.errors}
							</div>
						)}
					</Form>
				</div>
			</div>
		</main>
	)
}
